import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ControlForm, Modal } from "../../../components";
import { toggleMessage } from "../../../components/Toast/Toast";
import useAxios from "../../../components/UseAxios/useAxios";
import {
  ITaskCard,
  ITaskUpdateAssignRequest,
  ITaskUpdateDeadlinePesonalRequest
} from "../../../models/Task";
import { ITag } from "../../../models/user";
import TaskService from "../../../services/api/task.service";
import { TagInput } from "../../WorkflowManagement/components/TagInput/TagInput";
import i18next, { t } from "i18next";

import * as Yup from "yup";
import {
  LocalizationProvider,
  MobileDatePicker,
  MobileTimePicker
} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { FormHelperText } from "@mui/material";

interface IProps {
  open: boolean;
  task: ITaskCard;
  onUpdateSuccess: (newTask: ITaskCard) => void;
  handleClose: () => void;
}

interface DataForm {
  assign: ITag[];
  startDate: dayjs.Dayjs | null;
  endDate: dayjs.Dayjs | null;
}
const today = dayjs();

const DeadlinePesonalModal = ({
  open,
  task,
  onUpdateSuccess,
  handleClose
}: IProps) => {
  const updateAssignAxios = useAxios<ITaskCard>({ loading: "OnRequest" });
  const [locale, setLocale] = useState<string>();

  useEffect(() => {
    reset();
  }, [open]);

  useEffect(() => {
    setLocale(i18next.language);
    dayjs.locale(locale);
  }, [i18next.language]);

  useEffect(() => {
    if (updateAssignAxios)
      toggleMessage({
        type: "error",
        message: updateAssignAxios.error?.message ?? ""
      });
    if (updateAssignAxios.data) {
      toggleMessage({
        type: "success",
        message: updateAssignAxios?.message ?? ""
      });
      onUpdateSuccess(updateAssignAxios.data);
      handleClose();
    }
  }, [updateAssignAxios.error, updateAssignAxios.isSuccess]);

  const onSubmit = async (event: DataForm) => {
    const request: ITaskUpdateDeadlinePesonalRequest = {
      taskCardId: task.id,
      startDate: event.startDate
        ? dayjs.tz(event.startDate, "YYYY-MM-DDTHH:MM:SS", "Etc/GMT-7").format()
        : null,
      endDate: event.endDate
        ? dayjs.tz(event.endDate, "YYYY-MM-DDTHH:MM:SS", "Etc/GMT-7").format()
        : null
    };

    updateAssignAxios.request(TaskService.updateDeadlinePesonal(request));
  };

  const validationSchema = Yup.object().shape({
    startDate: Yup.date()
      .nullable()
      .required(t("Meeting.EnterStartTimeLabel"))
      .typeError(t("Meeting.EnterStartTimeLabel")),

    endDate: Yup.date()
      .nullable()
      .required(t("Meeting.EnterEndTimeLabel"))
      .typeError(t("Meeting.EnterEndTimeLabel"))
      .min(Yup.ref("startDate"), t("Meeting.EndDateMustBeAfterStartDate"))
  }) as any;
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<DataForm>({
    resolver: yupResolver(validationSchema)
  });

  return (
    <Modal
      title={t("Management.UpdatePersonalDeadlines")}
      textSubmit={t("Modal.SubmitText")}
      textClose={t("Modal.CancelText")}
      open={open}
      onSubmit={handleSubmit(onSubmit)}
      loadingState={updateAssignAxios.isLoading}
      disabledSubmit={updateAssignAxios.isLoading}
      onClose={
        !updateAssignAxios.isLoading
          ? () => {
              handleClose();
            }
          : undefined
      }
      width="500px"
    >
      <form
        style={{ display: "flex", flexDirection: "column", gap: "16px" }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div
          style={{
            display: "grid",
            gap: "16px",
            gridTemplateColumns: "repeat(2, 1fr)"
          }}
        >
          <ControlForm
            title={t("Meeting.StartTimeLabel")}
            classname="brand-form"
          >
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={locale}
            >
              <Controller
                name="startDate"
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error, invalid }
                }) => (
                  <>
                    <MobileDatePicker
                      disablePast
                      minDate={today}
                      value={value}
                      dayOfWeekFormatter={date => date.format("dd")}
                      onChange={newDate =>
                        onChange(
                          newDate
                            ? dayjs(newDate)
                                .hour(value ? value.hour() : 0)
                                .minute(value ? value.minute() : 0)
                            : null
                        )
                      }
                      slotProps={{
                        textField: {
                          placeholder: t("Management.ClickToSelectADate"),
                          InputProps: {
                            readOnly: true,
                            sx: {
                              cursor: "pointer",
                              "& .MuiSvgIcon-root": {
                                width: 24,
                                height: 24
                              }
                            }
                          }
                        },
                        toolbar: {
                          className: "dialog-toolbar",
                          sx: {
                            span: {
                              fontSize: 12
                            },
                            h4: {
                              fontSize: 20
                            }
                          }
                        },
                        calendarHeader: {
                          sx: {
                            "& .MuiPickersCalendarHeader-labelContainer": {
                              fontSize: 16
                            },
                            "& .MuiSvgIcon-root": {
                              width: 24,
                              height: 24
                            },
                            button: { fontSize: 18 }
                          }
                        },
                        mobilePaper: {
                          sx: {
                            "& .MuiDayCalendar-weekDayLabel": {
                              fontSize: 12
                            },
                            "& .MuiPickersDay-root": {
                              fontSize: 12
                            },
                            "& .MuiPickersMonth-monthButton": {
                              fontSize: 14
                            },
                            "& .MuiPickersYear-yearButton": {
                              fontSize: 14
                            },
                            "& .MuiButtonBase-root": {
                              fontSize: 14
                            }
                          }
                        }
                      }}
                    />

                    {error && (
                      <FormHelperText sx={{ color: "#ED3E47" }}>
                        {error.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
            </LocalizationProvider>
          </ControlForm>

          <ControlForm title={t("Meeting.EndTimeLabel")} classname="brand-form">
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={locale}
            >
              <Controller
                name="endDate"
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error, invalid }
                }) => (
                  <>
                    <MobileDatePicker
                      disablePast
                      minDate={today}
                      value={value}
                      dayOfWeekFormatter={date => date.format("dd")}
                      onChange={newDate =>
                        onChange(
                          newDate
                            ? dayjs(newDate)
                                .hour(value ? value.hour() : 0)
                                .minute(value ? value.minute() : 0)
                            : null
                        )
                      }
                      slotProps={{
                        textField: {
                          placeholder: t("Management.ClickToSelectADate"),
                          InputProps: {
                            readOnly: true,
                            sx: {
                              cursor: "pointer",
                              "& .MuiSvgIcon-root": {
                                width: 24,
                                height: 24
                              }
                            }
                          }
                        },
                        toolbar: {
                          className: "dialog-toolbar",
                          sx: {
                            span: {
                              fontSize: 12
                            },
                            h4: {
                              fontSize: 20
                            }
                          }
                        },
                        calendarHeader: {
                          sx: {
                            "& .MuiPickersCalendarHeader-labelContainer": {
                              fontSize: 16
                            },
                            "& .MuiSvgIcon-root": {
                              width: 24,
                              height: 24
                            },
                            button: { fontSize: 18 }
                          }
                        },
                        mobilePaper: {
                          sx: {
                            "& .MuiDayCalendar-weekDayLabel": {
                              fontSize: 12
                            },
                            "& .MuiPickersDay-root": {
                              fontSize: 12
                            },
                            "& .MuiPickersMonth-monthButton": {
                              fontSize: 14
                            },
                            "& .MuiPickersYear-yearButton": {
                              fontSize: 14
                            },
                            "& .MuiButtonBase-root": {
                              fontSize: 14
                            }
                          }
                        }
                      }}
                    />

                    {error && (
                      <FormHelperText sx={{ color: "#ED3E47" }}>
                        {error.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
            </LocalizationProvider>
          </ControlForm>
        </div>
      </form>
    </Modal>
  );
};

export default DeadlinePesonalModal;
